import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = {
  project_id: '', milestone_id: '', title: '', description: '',
  assigned_to: '', status: 'todo', priority: 'medium',
  estimated_hours: '', due_date: '',
};
const LIMIT = 15;
const STATUSES = ['todo', 'in_progress', 'review', 'done'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const statusColor = { todo: 'info', in_progress: 'primary', review: 'warning', done: 'active' };
const priorityColor = { low: 'info', medium: 'warning', high: 'primary', urgent: 'inactive' };

const KANBAN_COLS = [
  { key: 'todo',       label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'review',     label: 'Review' },
  { key: 'done',       label: 'Done' },
];

export default function Tasks({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'kanban'
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [users, setUsers]       = useState([]);

  useEffect(() => { setTitle('Tasks'); }, [setTitle]);
  useEffect(() => {
    api.get('/projects/all').then(r => setProjects(r.data)).catch(() => {});
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  // Load milestones when project changes in form
  useEffect(() => {
    if (!form.project_id) { setMilestones([]); return; }
    api.get(`/projects/${form.project_id}`).then(r => setMilestones(r.data.milestones || [])).catch(() => {});
  }, [form.project_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, page, limit: viewMode === 'kanban' ? 200 : LIMIT });
      if (filterProject) params.set('project_id', filterProject);
      if (filterStatus && viewMode === 'table') params.set('status', filterStatus);
      const { data } = await api.get(`/tasks?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [search, page, filterProject, filterStatus, viewMode]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => {
    setForm({
      project_id: item.project_id || '', milestone_id: item.milestone_id || '',
      title: item.title, description: item.description || '',
      assigned_to: item.assigned_to || '', status: item.status, priority: item.priority,
      estimated_hours: item.estimated_hours || '', due_date: item.due_date ? item.due_date.split('T')[0] : '',
    });
    setEditId(item.id); setModal(true);
  };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.project_id) return alert('Please select a project');
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/tasks/${editId}`, form);
      else        await api.post('/tasks', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving task');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete task "${title}"?`)) return;
    try { await api.delete(`/tasks/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const quickStatus = async (item, status) => {
    try {
      await api.put(`/tasks/${item.id}`, { ...item, status, project_id: item.project_id });
      load();
    } catch { /* ignore */ }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Tasks</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setViewMode('table')}
          >List</button>
          <button
            className={`btn ${viewMode === 'kanban' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setViewMode('kanban')}
          >Kanban</button>
          <button className="btn btn-primary" onClick={openAdd}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Task
          </button>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search tasks…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 180 }} value={filterProject} onChange={e => { setFilterProject(e.target.value); setPage(1); }}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {viewMode === 'table' && (
            <select className="form-control" style={{ width: 140 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
            </select>
          )}
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} tasks</span>
        </div>

        {loading ? <div className="loading">Loading…</div> : viewMode === 'kanban' ? (
          /* ── Kanban View ── */
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '0 0 8px' }}>
            {KANBAN_COLS.map(col => {
              const colItems = items.filter(t => t.status === col.key);
              return (
                <div key={col.key} style={{ minWidth: 240, flex: '0 0 240px', background: 'var(--bg-secondary)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{col.label}</span>
                    <span style={{ background: 'var(--border)', borderRadius: 10, padding: '1px 8px', fontSize: 12 }}>{colItems.length}</span>
                  </div>
                  {colItems.map(task => (
                    <div key={task.id} style={{ background: 'var(--bg)', borderRadius: 6, padding: '10px 12px', marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{task.title}</div>
                      {task.project_name && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{task.project_name}</div>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className={`badge badge-${priorityColor[task.priority]}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{task.priority}</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" style={{ padding: 2 }} onClick={() => openEdit(task)} title="Edit">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                      </div>
                      {col.key !== 'done' && (
                        <button style={{ marginTop: 6, fontSize: 11, background: 'none', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', width: '100%' }}
                          onClick={() => {
                            const next = STATUSES[STATUSES.indexOf(col.key) + 1];
                            if (next) quickStatus(task, next);
                          }}>
                          → Move to {KANBAN_COLS[KANBAN_COLS.indexOf(col) + 1]?.label}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Table View ── */
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th><th>Title</th><th>Project</th><th>Status</th><th>Priority</th>
                    <th>Assigned To</th><th>Est. Hours</th><th>Due Date</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={9} className="empty">No tasks found</td></tr>
                  ) : items.map((item, i) => (
                    <tr key={item.id}>
                      <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                      <td><strong>{item.title}</strong>{item.milestone_name && <span className="text-muted" style={{ fontSize: 11, marginLeft: 6 }}>📍 {item.milestone_name}</span>}</td>
                      <td className="text-muted">{item.project_name || '—'}</td>
                      <td><span className={`badge badge-${statusColor[item.status]}`} style={{ textTransform: 'capitalize' }}>{item.status.replace('_', ' ')}</span></td>
                      <td><span className={`badge badge-${priorityColor[item.priority]}`} style={{ textTransform: 'capitalize' }}>{item.priority}</span></td>
                      <td>{item.assignee_name || '—'}</td>
                      <td>{item.estimated_hours ? `${item.estimated_hours}h` : '—'}</td>
                      <td>{item.due_date ? item.due_date.split('T')[0] : '—'}</td>
                      <td>
                        <div className="actions">
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.title)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
                <div className="pagination-btns">
                  <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Task' : 'Add Task'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Project *</label>
                  <select className="form-control" value={form.project_id} onChange={e => f('project_id', e.target.value)}>
                    <option value="">— Select project —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Milestone</label>
                  <select className="form-control" value={form.milestone_id} onChange={e => f('milestone_id', e.target.value)}>
                    <option value="">— None —</option>
                    {milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Title *</label>
                  <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Task title" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select className="form-control" value={form.priority} onChange={e => f('priority', e.target.value)}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Estimated Hours</label>
                  <input type="number" className="form-control" value={form.estimated_hours} onChange={e => f('estimated_hours', e.target.value)} placeholder="0" min="0" step="0.5" />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" className="form-control" value={form.due_date} onChange={e => f('due_date', e.target.value)} />
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <textarea className="form-control" rows={3} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Task details…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Task' : 'Create Task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
